import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { IoPause, IoPlay } from "react-icons/io5";
import { api } from "../api/client";
import type { AIReadRow, Message, WorkflowTrialRow } from "../api/types";
import { todayNo } from "../customers";
import { liveTrials, replyBasis } from "../workflowOps";
import { useChatNotes, type ChatNote } from "../chatNotes";
import { useToday } from "../hooks";
import { PageShown } from "../pageShown";
import { fmtClock, fmtSecs } from "../util";
import { avatarStyle, describeMessage, initialOf, MEDIA_LINK_TTL_MS, revokedID, stickerDrawn, stickerLink } from "../ui";
import { modelName } from "./AIHealth";
import AvatarPic from "./AvatarPic";
import ChatViewer from "./ChatViewer";
import { AdPic } from "./CustomerBits";
import { Amt, stillWanted } from "./Kit";
import MediaIcon from "./MediaIcon";
import { ShellIcon } from "./ShellIcons";

/** The ad the customer tapped to start the chat, quoted over their first line (prototype v2.20 .chat-ref). */
export interface ChatAd {
  /** The campaign as the team names it: 「003 睡眠状态」. */
  name: string;
  /** The ad's own headline: 「失眠有几种情况」. */
  sub?: string;
  thumb?: string;
  /** When they tapped it: the card goes over their first line from then. */
  at?: string;
}

interface Props {
  messages: Message[];
  /** The scrolling chat pane; the transcript itself has no own scrollbar. */
  paneRef: React.RefObject<HTMLDivElement | null>;
  /** The customer, for the avatar on their voice notes. */
  peer: { waID: string; name: string; avatarURL?: string };
  /**
   * 回复: quote this line in the next message, as WhatsApp's own reply does
   * (owner, 2026-10-20). Absent for whoever may not write.
   */
  onReply?: (m: Message) => void;
  /**
   * 用草稿重跑: this reply of hers put again to the draft and to what is live (owner, 2026-11-03: the way an edit
   * is read now the exam does not hold 上线). Only on her replies; absent for whoever may not edit her.
   */
  onReplay?: (m: Message) => void;
  /** The ad they came from, when one is known for sure (not a guess). */
  ad?: ChatAd;
}

/** Who is drawn beside a bubble: the sender. */
interface Face {
  style: CSSProperties;
  initial: string;
  /** Drawn instead of the initial: 小爱's ✦, a colleague's headset. */
  icon?: ReactNode;
  /** Their picture, when there is one; drawn over the initial. */
  url?: string;
}

/** Our side: 小爱 a ✦ in her violet, a colleague a headset in the console's
 *  jade (prototype v2.20; the ✦ is the sidebar's own, QA G2-20) — the owner,
 *  2026-10-10: a 「同」 and an 「爱」 read oddly; 智能客服 and 人工客服 are what
 *  the trade calls the two. */
const AI_FACE: Face = {
  style: { background: "linear-gradient(140deg, var(--wc-violet), color-mix(in srgb, var(--wc-violet) 55%, #e879f9))" },
  initial: "",
  icon: <ShellIcon name="sparkles" size={13} />,
};
const STAFF_FACE: Face = { style: { background: "var(--brand)", color: "var(--on-brand)" }, initial: "", icon: <ShellIcon name="headset" size={13} /> };

/** Messages from the same side inside this gap are one group, as in the app. */
const GROUP_GAP_MS = 5 * 60 * 1000;

/** How long after a line WhatsApp lets its sender delete it for everyone: two days and twelve hours. */
const REVOKE_WINDOW_MS = 60 * 60 * 60 * 1000;

/** A line that went out through the provider: 小爱's, the script's, the system's, a colleague's from here — not one typed on the phone. */
const sentFromHere = (m: Message) => m.direction === "out" || (m.direction === "agent" && !!m.author && m.author !== "phone");

/**
 * The link comes from an unsigned webhook, so it is attacker-supplied text.
 * Anything that is not a plain https URL is dropped rather than handed to href —
 * `javascript:` in an anchor runs on click.
 */
function safeMediaHref(url?: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

/**
 * Resolve a stored attachment to a URL this page can render.
 *
 * The API needs a bearer token and an <img> cannot carry one, so the bytes are
 * fetched here and handed to the element as an object URL. The alternative — the
 * server minting a pre-signed link — would put the customer's photograph behind
 * a string that works for anyone holding it, which is what storing our own copy
 * was meant to stop.
 */
export function useStoredMedia(key?: string): { url: string | null; failed: boolean; type: string } {
  const [url, setUrl] = useState<string | null>(() => (key ? held.get(key)?.url ?? null : null));
  const [failed, setFailed] = useState(false);
  // The file's own type, so a voice note that will not play can say whether
  // it is this device's fault (an Ogg on an older iPhone) or the file's.
  const [type, setType] = useState(() => (key ? held.get(key)?.type ?? "" : ""));

  useEffect(() => {
    if (!key) return;
    let gone = false;
    let took = false;
    setFailed(false);
    if (!held.has(key)) setUrl(null);
    const [file, coming] = take(key);
    file
      .then((h) => {
        if (gone) {
          give(key);
          return;
        }
        took = true;
        setType(h.type);
        setUrl(h.url);
      })
      .catch(() => {
        if (!gone) setFailed(true);
      });
    return () => {
      gone = true;
      if (took) give(key);
      else if (coming) unwant(key, coming);
    };
  }, [key]);

  return { url, failed, type };
}

/**
 * The files of chats opened lately, by key, so going back to a chat shows its
 * pictures and voice notes at once (owner 10/16: 「对话在切换不同用户的时候也是卡卡的」).
 * Every switch used to download every file in the thread again — twenty in a
 * chat, up to 1.8 s each — and the pictures arrived one by one, moving the
 * messages under the reader. A key names its bytes for good (media keys are
 * content-addressed), so a kept copy is never stale. Kept to 150 files and
 * 80 MB, the least recently shown going first, and never one on screen.
 */
type Held = { url: string; type: string; size: number; refs: number; used: number };
const held = new Map<string, Held>();
/**
 * Files on their way, with how many on screen still want each. Tapping
 * through five chats used to download every picture of all five (19 files on
 * staging, 2026-10-21) for the one chat left open, on a phone's connection
 * shared with that chat's own. A file nobody has wanted for a second is
 * dropped — the server stops reading it too — and asked for again if its
 * chat is opened again; the second covers a chat left and come straight back to.
 */
type Fetching = { p: Promise<Held>; stop: AbortController; wanted: number; drop: number };
const fetching = new Map<string, Fetching>();
const DROP_AFTER_MS = 1000;
const HOLD_FILES = 150;
const HOLD_BYTES = 80 * 1024 * 1024;

/** The file, and the download it waits on when it is not here yet (for unwant). */
function take(key: string): [Promise<Held>, Fetching | undefined] {
  const h = held.get(key);
  let f = h ? undefined : fetching.get(key);
  if (!h && !f) {
    const stop = new AbortController();
    const p = api
      .media(key, stop.signal)
      .then((blob) => {
        const n: Held = { url: URL.createObjectURL(blob), type: blob.type, size: blob.size, refs: 0, used: Date.now() };
        held.set(key, n);
        return n;
      })
      .finally(() => {
        if (fetching.get(key)?.p === p) fetching.delete(key);
      });
    f = { p, stop, wanted: 0, drop: 0 };
    fetching.set(key, f);
  }
  if (f) {
    f.wanted++;
    window.clearTimeout(f.drop);
  }
  const file = Promise.resolve(h ?? f!.p).then((x) => {
    x.refs++;
    x.used = Date.now();
    trimHeld();
    return x;
  });
  return [file, f];
}

function give(key: string) {
  const h = held.get(key);
  if (!h) return;
  h.refs = Math.max(0, h.refs - 1);
  h.used = Date.now();
  trimHeld();
}

/** Gone before its file came. The last to go drops the download, a second later. */
function unwant(key: string, f: Fetching) {
  if (--f.wanted > 0 || fetching.get(key) !== f) return;
  window.clearTimeout(f.drop);
  f.drop = window.setTimeout(() => {
    if (f.wanted > 0 || fetching.get(key) !== f) return;
    fetching.delete(key);
    f.stop.abort();
  }, DROP_AFTER_MS);
}

function trimHeld() {
  let bytes = 0;
  for (const h of held.values()) bytes += h.size;
  if (held.size <= HOLD_FILES && bytes <= HOLD_BYTES) return;
  const idle = [...held.entries()].filter(([, h]) => h.refs === 0).sort((a, b) => a[1].used - b[1].used);
  for (const [k, h] of idle) {
    if (held.size <= HOLD_FILES && bytes <= HOLD_BYTES) break;
    URL.revokeObjectURL(h.url);
    held.delete(k);
    bytes -= h.size;
  }
}

/**
 * The ending a downloaded voice note is saved with, so the phone that could
 * not play it here knows what it is: from our copy's type, else the link's.
 */
function extOf(type: string, url?: string): string {
  const t = type.toLowerCase();
  if (/ogg|opus/.test(t)) return ".ogg";
  if (/mp4|m4a|aac/.test(t)) return ".m4a";
  if (/mpeg|mp3/.test(t)) return ".mp3";
  if (/wav/.test(t)) return ".wav";
  if (/webm/.test(t)) return ".webm";
  const path = /\.(ogg|opus|m4a|mp4|aac|mp3|wav|webm)(\?|$)/i.exec(url ?? "");
  return path ? `.${(path[1] ?? "").toLowerCase()}` : "";
}

/** The voice note that is playing: starting another stops it, as both apps do. */
let playingNow: HTMLAudioElement | null = null;

const RATES = [1, 1.5, 2];
const RATE_KEY = "wc-voice-rate";

/** The speed last chosen, kept per browser so a fast listener stays fast. */
function readRate(): number {
  try {
    const v = Number(localStorage.getItem(RATE_KEY));
    return RATES.includes(v) ? v : 1;
  } catch {
    return 1;
  }
}

/**
 * Why a voice note will not play, in one line. 小爱's voice is Ogg/Opus, which
 * Safari before iOS 18.4 cannot play at all; saying so stops a colleague on an
 * iPhone from reporting a recording that plays everywhere else as broken.
 */
function playFailure(err: unknown, a: HTMLAudioElement | null, src: string, type: string): string {
  const name = err instanceof DOMException ? err.name : "";
  const noOpus = !!a && a.canPlayType("audio/ogg; codecs=opus") === "";
  // Our own copies carry their type. A provider's link does not: WhatsApp's
  // end in .ogg, Messenger's are mp4, which every iPhone plays.
  const oggish = type ? /ogg|opus/i.test(type) : /\.(ogg|opus)(\?|$)/i.test(src);
  // 4 is MEDIA_ERR_SRC_NOT_SUPPORTED. On a provider link it is also what a
  // dead link reports, so only our own copy (a blob) reads it as the format.
  const unsupported = name === "NotSupportedError" || a?.error?.code === 4;
  if ((noOpus && oggish) || (unsupported && src.startsWith("blob:"))) return "这台设备放不了这种格式";
  return "语音无法播放";
}

/**
 * What both players share: play and pause, one note at a time, the speed, the
 * seek, and a failure that is said out loud. The old player dropped play()'s
 * rejection on the floor, so a note that would not play just did nothing.
 */
function useVoicePlayer(src: string | undefined, type: string, hint: number, speed: boolean) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [heard, setHeard] = useState(false);
  const [meta, setMeta] = useState(0);
  const [pos, setPos] = useState(0);
  const [error, setError] = useState("");
  // Both players have the speed button now (prototype v2.20's 「1×」); a player
  // without one must still not inherit a speed it cannot undo. Read when a note
  // starts, so the speed just picked on one note carries to the next one played.
  const chosenRate = () => (speed ? readRate() : 1);
  const [rate, setRate] = useState(chosenRate);
  // The audio element's own length when it has one; the decoded waveform's
  // when it does not (a stream can report Infinity).
  const dur = meta > 0 ? meta : hint;

  // The blob arriving after the fetch is a new file: start it clean.
  useEffect(() => {
    setError("");
    setPos(0);
    setMeta(0);
    setPlaying(false);
  }, [src]);
  // Playing: where it is, ten times a second, as v2.20's player steps its bars (chat.js ChatVoice);
  // the audio's own timeupdate comes only about four times a second.
  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      const a = ref.current;
      if (a) setPos(a.currentTime);
    }, 100);
    return () => window.clearInterval(id);
  }, [playing]);

  const fail = (err?: unknown) => {
    setError(playFailure(err, ref.current, src ?? "", type));
    setPlaying(false);
  };
  const toggle = () => {
    const a = ref.current;
    if (!a || !src) return;
    if (!a.paused) {
      a.pause();
      return;
    }
    setError("");
    const r = chosenRate();
    setRate(r);
    a.playbackRate = r;
    a.play().catch((err: unknown) => {
      // pause() landing before play() settled — another note was started, or
      // this one was tapped twice. That is the listener's doing, not a fault.
      if (err instanceof DOMException && err.name === "AbortError") return;
      fail(err);
    });
  };
  const seek = (t: number) => {
    const a = ref.current;
    if (!a || !(dur > 0)) return;
    const to = Math.max(0, Math.min(dur, t));
    a.currentTime = to;
    setPos(to);
  };
  const cycleRate = () => {
    const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length] ?? 1;
    setRate(next);
    if (ref.current) ref.current.playbackRate = next;
    try {
      localStorage.setItem(RATE_KEY, String(next));
    } catch {
      // Storage blocked (a private window): the speed holds for this page only.
    }
  };
  const length = (e: SyntheticEvent<HTMLAudioElement>) => {
    const d = e.currentTarget.duration;
    if (Number.isFinite(d) && d > 0) setMeta(d);
  };
  const stopped = (e: SyntheticEvent<HTMLAudioElement>) => {
    if (playingNow === e.currentTarget) playingNow = null;
    setPlaying(false);
  };
  const audio = {
    ref,
    src,
    onLoadedMetadata: length,
    onDurationChange: length,
    onTimeUpdate: (e: SyntheticEvent<HTMLAudioElement>) => setPos(e.currentTarget.currentTime),
    onPlay: (e: SyntheticEvent<HTMLAudioElement>) => {
      const a = e.currentTarget;
      if (playingNow && playingNow !== a) playingNow.pause();
      playingNow = a;
      // Loading a file resets the element's speed; the chosen one wins.
      a.playbackRate = chosenRate();
      setPlaying(true);
      setHeard(true);
    },
    onPause: stopped,
    onEnded: (e: SyntheticEvent<HTMLAudioElement>) => {
      stopped(e);
      setPos(0);
    },
    onError: () => fail(),
  };
  return { ref, audio, playing, heard, dur, pos, error, rate, toggle, seek, cycleRate };
}

/** The line a voice note shows instead of its length, with the file when there is one. */
function VoiceTrouble({ className, text, href, filename }: { className: string; text: string; href?: string; filename: string }) {
  return (
    <span className={className}>
      {text}
      {/* The file itself, for a device that cannot play it here. */}
      {href && (
        <a className="wc-voice-dl" href={href} download={filename} target="_blank" rel="noreferrer noopener">
          下载
        </a>
      )}
    </span>
  );
}

interface VoiceProps {
  /** Nothing to play: no copy was kept, or it could not be read. */
  src?: string;
  type: string;
  filename: string;
  preload: "metadata" | "none";
  /** Why there is nothing to play, when there is not. */
  note?: string;
}

/**
 * The bars a voice note draws. Sixty, of which the wave shows as many as stay thin (chat.css container
 * queries): thirty, the prototype's own count (chat.js chatWave), in its 268 px bubble; forty-five, then
 * all sixty, as a long 转文字 widens it. Thirty stretched across 490 px were 14 px blocks of one height
 * (owner, 2026-10-25, screenshots 21–22). No measuring: the bubble's width alone decides.
 */
const VOICE_BARS = 60;

/**
 * The bars' heights, 24–100 % as v2.20 draws them: the recording's own loudness, a slice's mean per bar,
 * or — with nothing to read — the prototype's own made-up shape (chat.js chatWave, its thirty bars on
 * the even ones), steady per note.
 */
function voiceHeights(peaks: number[] | null, seed: string, bars: number): number[] {
  if (!peaks) {
    let s = 0;
    for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) % 997;
    return Array.from({ length: bars }, (_, j) => {
      const i = j / 2;
      return Math.round(24 + 76 * Math.abs(Math.sin(s * 1.31 + i * 1.7) * 0.6 + Math.sin(i * 0.47 + s) * 0.4));
    });
  }
  return Array.from({ length: bars }, (_, i) => {
    const from = Math.floor((i * peaks.length) / bars);
    const to = Math.max(from + 1, Math.floor(((i + 1) * peaks.length) / bars));
    const part = peaks.slice(from, to);
    return Math.round(24 + 76 * (part.reduce((a, b) => a + b, 0) / Math.max(1, part.length)));
  });
}

/**
 * A voice note as prototype v2.20 draws one (chat.js ChatVoice): a round play
 * button in the sender's colour, the recording's own bars filling as it plays
 * (pressed, it plays from there), the length, and the speed 「1×」. The copy of
 * WhatsApp's player before it — the sender's face with a mic badge, a handle on
 * forty-six ticks — was not the design's (QA G2-24). Who sent it is the line
 * over it; what was said, the line under it.
 */
function VoiceNote({ src, type, filename, preload, note, waveKey, seed, mine }: VoiceProps & { waveKey?: string; seed: string; mine: boolean }) {
  const wave = useWave(waveKey, src);
  const v = useVoicePlayer(src, type, wave?.duration ?? 0, true);
  const heights = useMemo(() => voiceHeights(wave?.peaks ?? null, seed, VOICE_BARS), [wave, seed]);
  const frac = v.dur > 0 ? Math.min(1, v.pos / v.dur) : 0;
  const live = !!src && v.dur > 0;
  const trouble = note || v.error;
  const seekAt = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width > 0) v.seek(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * v.dur);
  };
  return (
    <div className={`wc-voice${mine ? " mine" : ""}${v.playing ? " playing" : ""}${src ? "" : " off"}`}>
      {src && <audio preload={preload} {...v.audio} />}
      <button type="button" className="wc-vplay" aria-label={v.playing ? "暂停" : "播放语音"} disabled={!src} onClick={v.toggle}>
        <ShellIcon name={v.playing ? "pause" : "play"} size={15} />
      </button>
      <span
        className="wc-wave"
        role="slider"
        aria-label="播放位置"
        aria-valuemin={0}
        aria-valuemax={Math.round(v.dur)}
        aria-valuenow={Math.round(v.pos)}
        aria-disabled={!live}
        tabIndex={live ? 0 : -1}
        onPointerDown={(e) => live && seekAt(e)}
        onKeyDown={(e) => {
          if (!live || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
          e.preventDefault();
          v.seek(v.pos + (e.key === "ArrowLeft" ? -5 : 5));
        }}
      >
        {heights.map((h, i) => (
          <i key={i} className={i / heights.length < frac ? "on" : ""} style={{ height: `${h}%` }} />
        ))}
      </span>
      {trouble ? (
        <VoiceTrouble className="wc-vt note" text={trouble} href={v.error ? src : undefined} filename={filename} />
      ) : (
        <span className="wc-vt">{fmtSecs(v.playing || v.pos > 0 ? v.pos : v.dur)}</span>
      )}
      <button type="button" className="wc-vsp" title="播放速度" disabled={!src} onClick={v.cycleRate}>
        {v.rate}×
      </button>
    </div>
  );
}

/** A note's loudness in 80 slices, 0–1, and its length. */
interface Wave {
  peaks: number[];
  duration: number;
}

/** Decoded once per stored file: a thread re-renders every 15 seconds. */
const waves = new Map<string, Promise<Wave>>();

const WAVE_SLICES = 80;

async function decodeWave(src: string): Promise<Wave> {
  const Ctx =
    window.OfflineAudioContext ??
    (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  if (!Ctx) throw new Error("no OfflineAudioContext");
  const bytes = await (await fetch(src)).arrayBuffer();
  // One frame is enough: the context is only a decoder here, never rendered.
  const buf = await new Ctx(1, 1, 22050).decodeAudioData(bytes);
  const data = buf.getChannelData(0);
  const size = Math.max(1, Math.floor(data.length / WAVE_SLICES));
  const peaks: number[] = [];
  for (let i = 0; i < WAVE_SLICES; i++) {
    const from = i * size;
    const to = Math.min(data.length, from + size);
    let sum = 0;
    for (let j = from; j < to; j++) {
      const s = data[j] ?? 0;
      sum += s * s;
    }
    peaks.push(Math.sqrt(sum / Math.max(1, to - from)));
  }
  // Spread from the quietest slice to the loudest: a note talked straight through is loud in every
  // quarter second, and scaled to the loudest alone its bars stood at one height.
  const top = Math.max(...peaks);
  const low = Math.min(...peaks);
  return { peaks: top > low ? peaks.map((p) => (p - low) / (top - low)) : peaks.map(() => 0), duration: buf.duration };
}

/** The real shape of a stored note, or null while it is read or when it cannot be. */
function useWave(key: string | undefined, src: string | undefined): Wave | null {
  const [wave, setWave] = useState<Wave | null>(null);
  useEffect(() => {
    if (!key || !src) return;
    let live = true;
    let p = waves.get(key);
    if (!p) {
      p = decodeWave(src);
      waves.set(key, p);
      // A failure is not kept: it may only have been the blob let go mid-read,
      // and the next look should try again.
      p.catch(() => waves.delete(key));
    }
    p.then(
      (w) => {
        if (live) setWave(w);
      },
      () => {
        // Undecodable here (an Ogg on an older iPhone): the seeded shape stays.
      },
    );
    return () => {
      live = false;
    };
  }, [key, src]);
  return wave;
}

/**
 * A made-up but steady shape for a note whose sound cannot be read: the same
 * key always draws the same bars, so the thread does not shimmer on refresh.
 */
function seededPeaks(seed: string): number[] {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  h = h || 1;
  const out: number[] = [];
  let prev = 0.5;
  for (let i = 0; i < WAVE_SLICES; i++) {
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    prev = prev * 0.45 + (((h >>> 0) % 1000) / 1000) * 0.55;
    out.push(prev);
  }
  return out;
}

/** The slices folded into the bars drawn, loudest wins, with a floor so silence still shows a stub. */
function barHeights(peaks: number[], bars: number): number[] {
  return Array.from({ length: bars }, (_, i) => {
    const from = Math.floor((i * peaks.length) / bars);
    const to = Math.max(from + 1, Math.floor(((i + 1) * peaks.length) / bars));
    const v = Math.max(...peaks.slice(from, to));
    return 0.12 + 0.88 * Math.sqrt(Math.max(0, v));
  });
}

const WAVE_H = 28;

/**
 * A voice note as Messenger draws one: a round-ended pill with no tail, a play
 * triangle, the waveform of the recording itself, and the time. No avatar, no
 * mic badge — the WhatsApp furniture in a Messenger thread read as the wrong
 * app. The played part is a second copy of the bars clipped by --p, moved every
 * frame while it plays, so the fill glides instead of stepping four times a
 * second.
 */
function MessengerVoice({ src, type, filename, preload, note, waveKey, seed }: VoiceProps & { waveKey?: string; seed: string }) {
  const wave = useWave(waveKey, src);
  const v = useVoicePlayer(src, type, wave?.duration ?? 0, true);
  const waveRef = useRef<HTMLSpanElement | null>(null);
  const bars = v.dur > 0 ? Math.max(16, Math.min(40, Math.round(v.dur * 3))) : 24;
  const heights = useMemo(() => barHeights(wave?.peaks ?? seededPeaks(seed), bars), [wave, seed, bars]);
  const durRef = useRef(v.dur);
  durRef.current = v.dur;

  // Playing: follow the audio every frame.
  useEffect(() => {
    const el = waveRef.current;
    const a = v.ref.current;
    if (!v.playing || !el || !a) return;
    let id = 0;
    const tick = () => {
      const d = durRef.current;
      el.style.setProperty("--p", String(d > 0 ? Math.min(1, a.currentTime / d) : 0));
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [v.playing, v.ref]);
  // Paused, ended or sought: where it stands.
  useEffect(() => {
    const el = waveRef.current;
    if (!el || v.playing) return;
    el.style.setProperty("--p", String(v.dur > 0 ? Math.min(1, v.pos / v.dur) : 0));
  }, [v.playing, v.pos, v.dur]);

  const seekAt = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width <= 0) return;
    const f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    v.seek(f * v.dur);
    e.currentTarget.style.setProperty("--p", String(f));
  };
  const live = !!src && v.dur > 0;
  const w = bars * 6 - 3;
  const rects = heights.map((h, i) => {
    const bh = Math.max(3, Math.round(h * WAVE_H));
    return <rect key={i} x={i * 6} y={(WAVE_H - bh) / 2} width={3} height={bh} rx={1.5} />;
  });
  const trouble = note || v.error;
  return (
    <div className={`wc-mv${src ? "" : " off"}`} style={{ "--bars": bars } as CSSProperties}>
      {src && <audio preload={preload} {...v.audio} />}
      <div className="wc-mv-pill">
        <button
          type="button"
          className="wc-mv-play"
          aria-label={v.playing ? "暂停" : "播放"}
          disabled={!src}
          onClick={v.toggle}
        >
          {v.playing ? <IoPause /> : <IoPlay />}
        </button>
        <span
          ref={waveRef}
          className="wc-mv-wave"
          role="slider"
          aria-label="播放位置"
          aria-valuemin={0}
          aria-valuemax={Math.round(v.dur)}
          aria-valuenow={Math.round(v.pos)}
          aria-disabled={!live}
          tabIndex={live ? 0 : -1}
          onPointerDown={(e) => {
            if (!live) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            seekAt(e);
          }}
          onPointerMove={(e) => {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) seekAt(e);
          }}
          onKeyDown={(e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            e.preventDefault();
            v.seek(v.pos + (e.key === "ArrowLeft" ? -5 : 5));
          }}
        >
          <svg className="wc-mv-bars" viewBox={`0 0 ${w} ${WAVE_H}`} preserveAspectRatio="none" aria-hidden="true">
            {rects}
          </svg>
          <svg className="wc-mv-bars done" viewBox={`0 0 ${w} ${WAVE_H}`} preserveAspectRatio="none" aria-hidden="true">
            {rects}
          </svg>
        </span>
        {/* The whole length at rest, what is left while it plays; with nothing
            to play, why — in the pill, where the length would be, so a note
            that cannot play does not read as one that is broken. */}
        <span className="wc-mv-time">{src ? fmtSecs(v.playing ? v.dur - v.pos : v.dur) : (note ?? "")}</span>
        {src && (v.playing || v.pos > 0) && (
          <button type="button" className="wc-mv-rate" title="播放速度" onClick={v.cycleRate}>
            {v.rate}x
          </button>
        )}
      </div>
      {src && trouble && <VoiceTrouble className="wc-mv-note" text={trouble} href={v.error ? src : undefined} filename={filename} />}
    </div>
  );
}

/**
 * A voice row, always drawn as a voice note — before, a row with nothing to
 * play fell back to a text bubble, and 小爱's voice lines read as 「🎤 」and a
 * sentence, as if she had typed them. With no recording the player is still
 * there, its button off, saying why.
 */
function Voice({ m, stored, fb }: { m: Message; stored: { url: string | null; failed: boolean; type: string }; fb: boolean }) {
  let src: string | undefined;
  let preload: "metadata" | "none" = "metadata";
  let note: string | undefined;
  if (m.media_key) {
    // Still being read: drawn quietly with the button off until it arrives.
    if (stored.failed) note = "附件已过期";
    else if (stored.url) src = stored.url;
  } else {
    // Nothing stored: the provider's link, which dies about a week after it
    // arrived — or, for 小爱's lines before we kept them, nothing at all.
    const href = safeMediaHref(m.media_url);
    if (!href) note = "这条没存录音";
    else if (Date.now() - new Date(m.at).getTime() > MEDIA_LINK_TTL_MS) note = "附件已过期";
    else {
      src = href;
      preload = "none";
    }
  }
  const p: VoiceProps = {
    src,
    type: m.media_key ? stored.type : "",
    filename: m.media_filename || `voice${extOf(m.media_key ? stored.type : "", m.media_url)}`,
    preload,
    note,
  };
  // Only our own copy is decoded for its shape; the provider's link is not
  // fetched just to draw bars.
  const waveKey = m.media_key && src ? m.media_key : undefined;
  const seed = m.media_key || m.media_url || m.msg_id || m.at;
  if (fb) return <MessengerVoice {...p} waveKey={waveKey} seed={seed} />;
  return <VoiceNote {...p} waveKey={waveKey} seed={seed} mine={m.direction !== "in"} />;
}

/** A sticker, or the word for one when its file will not load. */
function Sticker({ src }: { src: string }) {
  const [broken, setBroken] = useState(false);
  // The bubble around it is already see-through, so the word brings its own.
  if (broken) return <span className="wc-stk-gone">贴图</span>;
  return <img className="wc-stk" src={src} alt="贴图" loading="lazy" onError={() => setBroken(true)} />;
}

/**
 * A clip in the chat as prototype v2.20 draws one (chat.js ChatVideo): its first
 * frame under a big round play button, the length in a pill at the corner, a
 * thin bar along the foot. Pressed, it plays where it is with the browser's own
 * controls (seek, sound, full screen), which a cover of our own would only have
 * taken away. The browser's player at rest was the grey slab QA G2-24 saw.
 */
function Clip({ src, preload }: { src: string; preload: "metadata" | "none" }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [on, setOn] = useState(false);
  const [dur, setDur] = useState(0);
  const length = (e: SyntheticEvent<HTMLVideoElement>) => {
    const d = e.currentTarget.duration;
    if (Number.isFinite(d) && d > 0) setDur(d);
  };
  const start = () => {
    setOn(true);
    if (playingNow) playingNow.pause();
    ref.current?.play().catch(() => undefined);
  };
  return (
    <div className={`wc-vid${on ? " on" : ""}`}>
      <video ref={ref} src={src} preload={preload} playsInline controls={on} onLoadedMetadata={length} onDurationChange={length} />
      {!on && (
        <>
          <button type="button" className="wc-vid-play" aria-label="播放影片" onClick={start}>
            <ShellIcon name="play" size={20} />
          </button>
          <span className="wc-vid-d">
            <ShellIcon name="video" size={12} />
            {dur > 0 ? fmtSecs(dur) : "影片"}
          </span>
          <span className="wc-vid-bar" aria-hidden="true" />
        </>
      )}
    </div>
  );
}

/**
 * A picture in its bubble: pressed, the chat's own viewer opens over the page — never a new tab (QA G2-03).
 * Our own copy is in memory already, so it is drawn at once: lazy, it stayed 0 px tall until scrolled to and
 * then pushed the thread down under the reader. Only the provider's link waits to be near.
 */
function Pic({ src, onView }: { src: string; onView?: () => void }) {
  return (
    <button type="button" className="wc-img" title="看大图" onClick={onView}>
      <img src={src} alt="" loading={src.startsWith("blob:") ? "eager" : "lazy"} />
    </button>
  );
}

/** A file that is not a picture, a clip or a voice note (prototype v2.20 .chat-file): its tile, its name, what kind. */
function FileLink({ href, label, download }: { href: string; label: string; download?: string }) {
  const ext = /\.([a-z0-9]{1,5})$/i.exec(label)?.[1]?.toUpperCase();
  return (
    <a className="wc-file" href={href} download={download} target="_blank" rel="noreferrer noopener">
      <span className="wc-file-i">
        <ShellIcon name="doc" size={18} />
      </span>
      <span className="wc-file-t">
        <b>{label}</b>
        <small>{ext ? `${ext} 文件` : "文件"}</small>
      </span>
    </a>
  );
}

/**
 * The attachment on a message — the customer's, a colleague's from the phone,
 * or one sent from this console. Pictures, clips and voice notes render inline;
 * anything else is a link.
 */
function Attachment({ m, fb, onView }: { m: Message; fb: boolean; onView?: () => void }) {
  const stored = useStoredMedia(m.media_key);
  const label = m.media_filename || m.raw_type?.toLowerCase() || "file";
  const kind = (m.raw_type || "").toUpperCase();

  if (kind === "AUDIO" || kind === "VOICE") return <Voice m={m} stored={stored} fb={fb} />;

  // Our own copy first: it does not expire, so a photograph from last month is
  // still a photograph.
  if (m.media_key) {
    if (stored.failed) return <div className="wc-gone">附件已过期</div>;
    // A picture or a clip on its way holds a picture's room, so the messages
    // under it do not jump down when it lands.
    if (!stored.url) return <div className={`wc-gone${kind === "IMAGE" || kind === "VIDEO" ? " wc-media-wait" : ""}`}>读取中…</div>;
    if (kind === "IMAGE") return <Pic src={stored.url} onView={onView} />;
    if (kind === "STICKER") return <Sticker src={stored.url} />;
    if (kind === "VIDEO") return <Clip src={stored.url} preload="metadata" />;
    return <FileLink href={stored.url} label={label} download={label} />;
  }

  // Nothing stored: the provider's link is all this row has, and it dies about
  // a week after it arrived. Say which of the two an operator is looking at.
  const href = safeMediaHref(m.media_url || stickerLink(m));
  if (!href) return null;
  if (Date.now() - new Date(m.at).getTime() > MEDIA_LINK_TTL_MS) {
    // A sticker already says 贴图 in its text; "expired" would be noise on one.
    if (kind === "STICKER") return null;
    return (
      <div className="wc-gone" title="服务商的链接大约一周后就失效">
        附件已过期
      </div>
    );
  }
  if (kind === "IMAGE") return <Pic src={href} onView={onView} />;
  if (kind === "STICKER") return <Sticker src={href} />;
  if (kind === "VIDEO") return <Clip src={href} preload="none" />;
  return <FileLink href={href} label={label} />;
}

/**
 * The day a message belongs to, as WhatsApp writes it. A thread read back a
 * week later is a wall of times with no days between them, and "11:26" means
 * nothing without one.
 */
function dayLabel(at: string): string {
  const d = new Date(at);
  const today = new Date();
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "今天";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (same(d, yesterday)) return "昨天";
  // 「10月13日 周二」 as prototype v2.20 writes it; the year only when it is not this one.
  const year = d.getFullYear() === today.getFullYear() ? "" : `${d.getFullYear()}年`;
  return `${year}${d.getMonth() + 1}月${d.getDate()}日 ${WEEK[d.getDay()]}`;
}
const WEEK = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

/**
 * The conversation. New messages arriving on the 15s poll only pull the pane
 * down if the reader was already at the bottom — otherwise someone reading back
 * through the history would be yanked away mid-sentence.
 */
/**
 * The sender's name for a business-side message, in the words the team
 * uses. The machines have fixed names; a colleague is shown as they were
 * added. Rows from before the column existed fall back to the old two
 * labels, so an old thread still reads.
 */
/**
 * Whether 小爱 sent it — the AI, her script, her follow-up, or a row from
 * before the author column that was not marked a colleague's. Her bubbles are
 * violet: the owner, 2026-10-10, 「现在的不仔细看根本看不出是谁回的」.
 */
export function bySiaoAi(m: Pick<Message, "direction" | "author">): boolean {
  return m.direction !== "in" && authorLabel(m).startsWith("小爱");
}

export function authorLabel(m: Pick<Message, "direction" | "author">): string {
  switch (m.author) {
    case "ai":
      return "小爱";
    case "playbook":
      return "小爱 · 剧本";
    // She wrote to a customer who had gone quiet: not an answer to anything.
    case "followup":
      return "小爱 · 跟进";
    case "system":
      return "系统";
    // The five agents share one WhatsApp number and neither provider tells us
    // which of them typed it, so this says so rather than implying a person.
    case "phone":
      return "客服 · 手机";
    case undefined:
    case "":
      // Older rows predate the author column (2026-09-05); nothing to recover.
      return m.direction === "agent" ? "客服" : "小爱";
    default:
      return m.author;
  }
}


/** ✓, ✓✓ or a red mark, as prototype v2.20 draws a line's ticks (ChatTicks); nothing where nothing was reported. */
function TickIcon({ tick }: { tick: NonNullable<Message["tick"]> }) {
  if (tick === "unknown") return null;
  if (tick === "failed") return <ShellIcon name="alert" size={14} className="wc-tick bad" />;
  return <ShellIcon name={tick === "sent" ? "check" : "check2"} size={15} className={`wc-tick${tick === "read" ? " read" : ""}`} />;
}
const TICK_SAY: Record<NonNullable<Message["tick"]>, string> = { sent: "已发出", delivered: "已送到", read: "已读", failed: "没送到", unknown: "" };

/** 「14:32」 today, 「10/11 14:32」 any other day. */
function shortWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (d.toDateString() === new Date().toDateString()) return fmtClock(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${fmtClock(iso)}`;
}

/** One of 发出 / 送到 / 已读: its tick in a ring, the word, the time. */
function TimeStep({ tone, icon, word, when }: { tone: "" | "done" | "read" | "bad"; icon: "check" | "check2" | "alert"; word: string; when: string }) {
  return (
    <span className={`wc-ts${tone ? ` ${tone}` : ""}`}>
      <i>
        <ShellIcon name={icon} size={icon === "check2" ? 14 : 13} />
      </i>
      <b>{word}</b>
      <em>{when}</em>
    </span>
  );
}

/**
 * When a line left, reached their phone and was read, under its bubble, drawn as
 * the three ticks it is — ✓ 发出 → ✓✓ 送到 → blue ✓✓ 已读, the lines between them
 * drawing in (prototype v2.20 ChatTimes; QA G2-15: it was two columns of small
 * words). It scrolls itself into view when it opens under the last line.
 */
function TickTimes({ m }: { m: Message }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    const pane = el?.closest(".wc-body");
    if (!el || !pane) return;
    const over = el.getBoundingClientRect().bottom + 14 - pane.getBoundingClientRect().bottom;
    if (over > 0) pane.scrollBy({ top: over, behavior: stillWanted() ? "auto" : "smooth" });
  }, []);
  const sent = <TimeStep tone="done" icon="check" word="发出" when={shortWhen(m.at)} />;
  // A line typed on the phone: WhatsApp tells the system nothing of it (10/12, production: none of
  // them had a report). A lone ✓ there read as 「not delivered yet」.
  if (m.tick === "unknown") {
    return (
      <div className="wc-times" role="status" ref={ref}>
        <div className="wc-ts-row">{sent}</div>
        <p>{m.author === "phone" ? "手机上发的，WhatsApp 不告诉系统送到、读了没有，要看手机" : "这句没有 WhatsApp 的回报"}</p>
      </div>
    );
  }
  if (m.tick === "failed") {
    return (
      <div className="wc-times" role="status" ref={ref}>
        <div className="wc-ts-row">
          {sent}
          <span className="wc-ts-l bad" />
          <TimeStep tone="bad" icon="alert" word="送到" when="没送到" />
        </div>
        <p className="bad">WhatsApp 没送到这条</p>
      </div>
    );
  }
  const reached = m.tick === "delivered" || m.tick === "read";
  const read = m.tick === "read";
  return (
    <div className="wc-times" role="status" ref={ref}>
      <div className="wc-ts-row">
        {sent}
        <span className={`wc-ts-l${reached ? " done" : ""}`} />
        <TimeStep tone={reached ? "done" : ""} icon="check2" word="送到" when={m.delivered_at ? shortWhen(m.delivered_at) : reached ? "送到了" : "还没送到"} />
        <span className={`wc-ts-l${read ? " read" : ""}`} />
        {/* Read because they answered: when they answered, said as that, never as the moment it was read. */}
        <TimeStep tone={read ? "read" : ""} icon="check2" word="已读" when={m.read_by_reply ? "读了" : m.read_at ? shortWhen(m.read_at) : read ? "读了" : "还没读"} />
      </div>
      {m.read_by_reply && m.read_at && <p>客户 {shortWhen(m.read_at)} 回了话 · 之前的都读了</p>}
      {m.tick === "delivered" && <p>客户关了「已读回执」的话，永远不会变蓝</p>}
    </div>
  );
}

/**
 * Which of 小爱's messages each kept read goes under: the first of hers from
 * a moment before it was kept (the reply is written, then sent) to three
 * minutes after (a voice note takes a while), each message one read.
 */
export function readsUnder(messages: Message[], reads: AIReadRow[]): Map<number, AIReadRow> {
  const out = new Map<number, AIReadRow>();
  const sorted = [...reads].filter((r) => r.read && Object.keys(r.read).length > 0).sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  for (const r of sorted) {
    const t = Date.parse(r.at);
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i]!;
      if (!bySiaoAi(m) || out.has(i)) continue;
      const d = Date.parse(m.at) - t;
      if (d >= -5_000 && d <= 180_000) {
        out.set(i, r);
        break;
      }
    }
  }
  return out;
}

/** The four steps 小爱's workflow walks (prototype v2.20 CHAT_STEPS); her read names one, or says it in words. */
const STEPS = ["开场", "介绍", "报价", "收单"];

/**
 * 小爱's read over her reply (prototype v2.20 ChatThink): one quiet card in her
 * violet — what holds them back, what this line is for — opening to all four.
 * A row of its own before the reply it went with, at a reading width, as the
 * prototype puts it (what she thought, then what she said); it sat under the reply.
 */
function ReadCard({ row, trials, open, onToggle }: { row: AIReadRow; trials: WorkflowTrialRow[]; open: boolean; onToggle: () => void }) {
  const r = row.read ?? {};
  // What stood behind the reply: its step and way, and that step's numbers now (owner 11/2: 「有数据依据的不是凭感觉？」).
  const src = replyBasis(row, trials, todayNo());
  const at = r.stage ? STEPS.findIndex((s) => r.stage!.includes(s)) : -1;
  return (
    <div className="wc-msg out think">
      <div className="wc-stack">
        <div className={`wc-think${open ? " open" : ""}`}>
          <button type="button" className="wc-think-h" aria-expanded={open} onClick={onToggle}>
            <ShellIcon name="sparkles" size={14} />
            <b>小爱这样想</b>
            {!open && (r.worry || r.aim) && (
              <span className="wc-think-1">
                {r.worry ? `卡在：${r.worry}` : ""}
                {r.aim ? `${r.worry ? " → " : ""}${r.aim}` : ""}
              </span>
            )}
            <span className={`wc-think-src ${src.mark}`} title={src.basis}>
              {src.mark === "trial" ? "试验中" : src.mark === "kept" ? "试过" : "没数据"}
            </span>
            <ShellIcon name="down" size={15} className="wc-think-c" />
          </button>
          {open && (
            <div className="wc-think-b">
              {r.want && (
                <div>
                  <span>他要</span>
                  <b>{r.want}</b>
                </div>
              )}
              {r.worry && (
                <div>
                  <span>卡在</span>
                  <b>{r.worry}</b>
                </div>
              )}
              {r.stage && (
                <div>
                  <span>走到</span>
                  {at >= 0 ? (
                    <span className="wc-steps">
                      {STEPS.map((s, i) => (
                        <i key={s} className={i < at ? "done" : i === at ? "now" : ""}>
                          {i < at && <ShellIcon name="check" size={11} />}
                          {s}
                        </i>
                      ))}
                    </span>
                  ) : (
                    <b>{r.stage}</b>
                  )}
                </div>
              )}
              {r.aim && (
                <div>
                  <span>这一句</span>
                  <b>要他{r.aim}</b>
                </div>
              )}
              <div className="wc-think-tag">
                {src.took} · {row.asked ? "最后留了问题" : "没有留问题"}
              </div>
              <div className={`wc-think-basis ${src.mark}`} title={src.tip}>
                {src.basis}
              </div>
            </div>
          )}
        </div>
      </div>
      <span className="wc-our-face ghost" aria-hidden="true" />
    </div>
  );
}

/** 小爱's order card, read back into its parts: what is bought, the shop's figures, who and where, when it comes. */
export interface OrderSlip {
  /** Her words over the card, when they came in the same message. */
  lead: string;
  items: { name: string; n: string }[];
  gifts: string[];
  /** 运费: where it is free (西马) or the fee; null when the card said nothing of it. */
  ship: { where: string; fee: string } | null;
  total: string;
  pay: string;
  /** Who it is for, then the address. */
  to: string[];
  /** 「寄出后一般 2–3 天到」. */
  when: string;
}

const SLIP_HEAD = /^📋\s*(请确认订单|订单|Please check your order)$/;
const SLIP_MONEY = /RM\s?[\d,]+(?:\.\d+)?/;

/** 「ZENITE 助眠咀嚼片 3 盒」 or 「ZENITE × 3」: the product and how many. */
function slipItem(l: string): { name: string; n: string } {
  const m = /^(.*\S)\s*×\s*(\d+)$/.exec(l) ?? /^(.*\S)\s+(\d+)\s*\S{0,3}$/.exec(l);
  return m ? { name: m[1]!, n: m[2]! } : { name: l, n: "" };
}

/**
 * The order card 小爱 sends for the customer to confirm (api gemini/card.go cardBody: 「📋 请确认订单」, a line
 * a product, the delivery, what is paid and how; then who and where; then when it comes and what to press), in
 * its parts, so the chat can draw it as the receipt it is (prototype v2.20 .chat-rc). Null for any other line,
 * and for a card in a shape this does not know: that one is shown as the words it is.
 */
export function orderSlip(text?: string): OrderSlip | null {
  if (!text || !text.includes("📋")) return null;
  const lines = text.split("\n").map((l) => l.trim());
  const head = lines.findIndex((l) => SLIP_HEAD.test(l));
  if (head < 0) return null;
  const slip: OrderSlip = { lead: lines.slice(0, head).join("\n").trim(), items: [], gifts: [], ship: null, total: "", pay: "", to: [], when: "" };
  // The card's blocks, a blank line between: what is bought and paid, who and where, the rest.
  let block = 0;
  for (const l of lines.slice(head + 1)) {
    if (!l) {
      if ((block === 0 && slip.items.length) || (block === 1 && slip.to.length)) block++;
      continue;
    }
    // How an order paid after the press is paid (its line names the button too).
    if (block === 0 && /^(按「|After you tap)/.test(l)) slip.pay = "网上付款";
    else if (block === 0 && /^(银行转账|Bank transfer)/.test(l)) slip.pay = "银行转账";
    // What to press: the buttons under the card say it.
    else if (/「✅|对的话/.test(l)) continue;
    else if (/^🚚/.test(l)) slip.when = l.replace(/^🚚\s*/, "");
    else if (block === 1) slip.to.push(l);
    else if (block > 1 || /^💰/.test(l)) continue;
    else if (/^🎁/.test(l)) slip.gifts.push(l.replace(/^🎁\s*(额外送[：:]|Free gift:)?\s*/, ""));
    else if (/^(西马包邮|包邮|Free delivery)/.test(l)) slip.ship = { where: /西马|West/.test(l) ? "西马" : "", fee: "" };
    else if (/^(运费|Delivery)\s/.test(l)) slip.ship = { where: "", fee: SLIP_MONEY.exec(l)?.[0] ?? l.replace(/^\S+\s*/, "") };
    else if (/^(货到付款|Cash on delivery)/.test(l)) {
      slip.total = SLIP_MONEY.exec(l)?.[0] ?? "";
      slip.pay = "货到付款 · 东西到了才付钱";
    } else if (/^(总共|Total)/.test(l)) slip.total = SLIP_MONEY.exec(l)?.[0] ?? "";
    else slip.items.push(slipItem(l));
  }
  return slip.items.length ? slip : null;
}

/** The card as the receipt it is (prototype v2.20 .chat-rc): a slip in her bubble, torn across above the total. */
function OrderSlipView({ slip, at }: { slip: OrderSlip; at: string }) {
  const d = new Date(at);
  return (
    <>
      {slip.lead && <span className="wc-txt wc-rc-lead">{slip.lead}</span>}
      <div className="wc-rc" role="group" aria-label={`订单确认${slip.total ? `，一共 ${slip.total}` : ""}`}>
        <div className="wc-rc-h">
          <span className="wc-rc-t">
            <ShellIcon name="receipt" size={15} />
            订单确认
          </span>
          <span className="wc-rc-d">
            {d.getMonth() + 1}月{d.getDate()}日 {fmtClock(at)}
          </span>
        </div>
        <div className="wc-rc-ls">
          {slip.items.map((it, k) => (
            <div className="wc-rc-l" key={k}>
              <span className="wc-rc-i">
                {it.name}
                {it.n && <small>×{it.n}</small>}
              </span>
            </div>
          ))}
          {slip.gifts.map((g, k) => (
            <div className="wc-rc-l" key={`g${k}`}>
              <span className="wc-rc-i">{g}</span>
              <span className="wc-rc-free">额外送</span>
            </div>
          ))}
          {slip.ship && (
            <div className="wc-rc-l">
              <span className="wc-rc-i">运费{slip.ship.where && ` · ${slip.ship.where}`}</span>
              {slip.ship.fee ? <Amt text={slip.ship.fee} className="wc-rc-a" /> : <span className="wc-rc-free">包邮</span>}
            </div>
          )}
        </div>
        {slip.total && (
          <div className="wc-rc-sum">
            <span>一共</span>
            <Amt text={slip.total} className="wc-rc-amt" />
          </div>
        )}
        {(slip.pay || slip.to.length > 0 || slip.when) && (
          <dl className="wc-rc-f">
            {slip.pay && (
              <>
                <dt>付款</dt>
                <dd>{slip.pay}</dd>
              </>
            )}
            {slip.to.length > 0 && (
              <>
                <dt>寄到</dt>
                <dd>{slip.to.join("\n")}</dd>
              </>
            )}
            {slip.when && (
              <>
                <dt>送到</dt>
                <dd>{slip.when}</dd>
              </>
            )}
          </dl>
        )}
      </div>
    </>
  );
}

/** A picture's file is here to be drawn: our own copy, or the provider's link inside its week. */
function pictureShown(m: Message): boolean {
  if ((m.raw_type || "").toUpperCase() !== "IMAGE") return false;
  if (m.media_key) return true;
  return !!safeMediaHref(m.media_url) && Date.now() - new Date(m.at).getTime() <= MEDIA_LINK_TTL_MS;
}

/** The picture in the viewer, from the chat's own copy (held while its bubble is on screen, so it is there at once). */
function ViewerPic({ m }: { m: Message }) {
  const stored = useStoredMedia(m.media_key);
  const src = m.media_key ? stored.url : safeMediaHref(m.media_url);
  if (m.media_key && stored.failed) return <span className="wc-view-wait">附件已过期</span>;
  if (!src) return <span className="wc-view-wait">读取中…</span>;
  return <img src={src} alt="" className="photo" />;
}

/** Chats whose next read should land on the newest line: the agent just sent one (VoiceComposer). */
const following = new Set<string>();
/** How long after the reader's own wheel, finger or key a scroll of the chat is still theirs. */
const READER_MS = 1000;
/** The next read of this chat goes to its newest line wherever the reader was, as WhatsApp does after a send. */
export function followNext(waID: string) {
  following.add(waID);
}

/** A row's own name across reads: the server's window drops lines off the top, so the index is not one. */
function keyOf(m: Message): string {
  return m.ref || m.msg_id || `${m.at}|${m.direction}|${(m.text ?? "").slice(0, 24)}`;
}

/** How long a line that just came keeps its arrival class: the slide (.38s) and a little. */
const FRESH_MS = 900;

/**
 * How many of a chat's newest lines are drawn when it opens, and how many older ones join each time the reader
 * comes near the top — as WhatsApp draws a chat. Every line was drawn at once (the API sends up to 500): a
 * 560-line chat took 0.4–1.3 s to open on the owner's screen, most of it laying out 15,000 boxes, and every
 * notch of the wheel hit-tested and painted all of them (2026-10-28, 「对话也感觉卡卡的」).
 */
const FIRST_LINES = 60;
/** 80 at a time were a 0.12–0.18 s frame under the wheel; 40 are half that, twice as often. */
const MORE_LINES = 40;
/** How near the top, in pixels, the older lines are drawn: they are there before the reader is. */
const LINES_AHEAD = 900;

type Row = { k: "msg"; i: number; key: string; at: string } | { k: "note"; n: ChatNote; key: string; at: string };

export default function Transcript({ messages, paneRef, peer, onReply, onReplay, ad }: Props) {
  // The test LeadsView uses to dress the pane as Messenger (.wc.fb). Every
  // rule of the Messenger voice pill hangs off that class, so a second test
  // that disagreed would draw the pill with no shape at all.
  const fb = peer.waID.startsWith("fb:");
  // 今天 / 昨天 over the days turn over at midnight by themselves: a poll
  // that brings nothing new no longer draws the chat again.
  useToday();
  // Whose sent / delivered / read times are open under their bubble: one at a time, closed again by
  // a second press or Escape (owner 10/12: 「可以看见顾客几点读的」).
  const [timesOf, setTimesOf] = useState<number | null>(null);
  useEffect(() => setTimesOf(null), [peer.waID]);
  useEffect(() => {
    if (timesOf === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTimesOf(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [timesOf]);
  // 小爱's read of the customer before each reply (api gemini/read.go), put
  // under the first bubble of the reply it went with: the owner, 2026-10-12,
  // wants to see what she thought, so a wrong read is seen, not guessed at.
  const aiCount = useMemo(() => messages.filter((m) => bySiaoAi(m)).length, [messages]);
  const [reads, setReads] = useState<AIReadRow[]>([]);
  const [openRead, setOpenRead] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    if (aiCount === 0) {
      setReads([]);
      return;
    }
    api.leadAiReads(peer.waID).then(
      (r) => live && setReads(r.reads ?? []),
      () => live && setReads([]),
    );
    return () => {
      live = false;
    };
  }, [peer.waID, aiCount]);
  const readAt = useMemo(() => readsUnder(messages, reads), [messages, reads]);
  // The live trials, for each read to say what its step stands on (workflowOps replyBasis); read once a few minutes.
  const [trials, setTrials] = useState<WorkflowTrialRow[]>([]);
  useEffect(() => {
    if (reads.length === 0) return;
    let live = true;
    void liveTrials().then((t) => live && setTrials(t));
    return () => {
      live = false;
    };
  }, [reads.length]);
  // The last line of ours they read: 「已读 14:32」 goes under it, as Messenger says 「Seen」.
  const lastRead = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]!;
      if (m.direction !== "in" && m.tick === "read" && m.read_at) return i;
    }
    return -1;
  }, [messages]);
  // A reaction names the message it reacted to, so it belongs ON that
  // message — a small chip under the bubble, as both apps draw it — and not
  // as a line of its own saying somebody reacted to something.
  const reactions = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const msg of messages) {
      if ((msg.raw_type || "").toUpperCase() !== "REACTION") continue;
      try {
        const o = JSON.parse((msg.text ?? "").trim()) as { emoji?: string; messageId?: string };
        const emoji = (o.emoji ?? "").trim();
        if (!emoji || !o.messageId) continue;
        m.set(o.messageId, [...(m.get(o.messageId) ?? []), emoji]);
      } catch {
        // Not the shape we know; it stays a line of its own below.
      }
    }
    return m;
  }, [messages]);
  // Which reaction rows found their message. The rest still render as a
  // note, because a reaction to something outside the loaded window is
  // still something that happened.
  const placed = useMemo(() => {
    const ids = new Set(messages.map((m) => m.msg_id).filter(Boolean) as string[]);
    const out = new Set<string>();
    for (const id of reactions.keys()) if (ids.has(id)) out.add(id);
    return out;
  }, [messages, reactions]);
  // The choice the customer pressed under a message: their next line is its words (the API keeps a press's
  // title for the chat, whatsapp/interactive.go), ticked under the message as v2.20 marks it (.chat-choice.on).
  const pressed = useMemo(() => {
    const out = new Map<number, string>();
    const said = (s?: string) => (s ?? "").replace(/\s+/g, " ").trim().toLowerCase();
    messages.forEach((m, i) => {
      if (!m.buttons?.length) return;
      for (let j = i + 1; j < messages.length; j++) {
        const x = messages[j]!;
        if (x.direction !== "in" || (x.raw_type || "").toUpperCase() === "REACTION") continue;
        const b = m.buttons.find((y) => said(y) === said(x.text));
        if (b) out.set(i, b);
        break;
      }
    });
    return out;
  }, [messages]);
  // The lines a reply can quote, by the id it quotes them by: a reply draws
  // the line it answered above its words, as both apps do.
  const byRef = useMemo(() => {
    const m = new Map<string, number>();
    messages.forEach((x, i) => {
      if (x.ref) m.set(x.ref, i);
    });
    return m;
  }, [messages]);
  // What a revoke took back (owner, 10/31: 「撤回了一条消息」 and nobody could tell what). WhatsApp names the line by
  // the id a reply quotes it by: the customer's own lines and those typed on the phone are still here, drawn faded
  // under 「已撤回」, and the grey line goes to them when pressed. 小爱's and the console's lines are named by
  // WhatsApp's own wamid, which the provider never hands us for a send (3 of 26 revokes in 50 chats, 10/31): the
  // grey line can say they were hers, not which. Not msg_id — 小爱's line carries the id of the one it answered.
  const revokes = useMemo(() => {
    const took = new Map<number, number>();
    const gone = new Set<number>();
    const hers = new Set<number>();
    messages.forEach((m, i) => {
      const id = revokedID(m);
      if (!id) return;
      const t = byRef.get(id);
      if (t !== undefined && t !== i) {
        took.set(i, t);
        gone.add(t);
        return;
      }
      if (m.direction === "in" || !id.startsWith("wamid.")) return;
      const at = Date.parse(m.at);
      const sent = messages.slice(0, i).filter((x) => sentFromHere(x) && at - Date.parse(x.at) < REVOKE_WINDOW_MS);
      if (sent.length > 0 && sent.every(bySiaoAi)) hers.add(i);
    });
    return { took, gone, hers };
  }, [messages, byRef]);
  // The quoted line, flashed when its quote is pressed and the chat goes to it (prototype: 1.3 s). One above the
  // lines drawn (FIRST_LINES) is drawn first, and gone to once it is.
  const [flash, setFlash] = useState<number | null>(null);
  const jumpOwed = useRef<number | null>(null);
  // At once to a line just drawn: a glide's first frame moves a few pixels, still at the end to the scroll handler,
  // and the pictures of the lines drawn above, loading, then took the reader back down.
  function goTo(i: number, now = false) {
    atBottom.current = false;
    chasing.current = false;
    paneRef.current?.querySelector(`[data-i="${i}"]`)?.scrollIntoView({ block: "center", behavior: now || stillWanted() ? "auto" : "smooth" });
    setFlash(i);
    window.setTimeout(() => setFlash((f) => (f === i ? null : f)), 1300);
  }
  function jumpTo(i: number) {
    const at = rowAt.get(i) ?? 0;
    if (at < from) {
      jumpOwed.current = i;
      setDrawn(rows.length - at + MORE_LINES / 4);
      return;
    }
    goTo(i);
  }
  // A phone has no hover: the line held down shows its buttons, until a press
  // anywhere else.
  const [acting, setActing] = useState<number | null>(null);
  useEffect(() => setActing(null), [peer.waID]);
  useEffect(() => {
    if (acting === null) return;
    const away = (e: PointerEvent) => {
      if (!(e.target as Element | null)?.closest?.(".wc-acts")) setActing(null);
    };
    document.addEventListener("pointerdown", away, true);
    return () => document.removeEventListener("pointerdown", away, true);
  }, [acting]);
  // A finger on a line: hold it for its buttons; swipe it right to answer it,
  // as in WhatsApp. A mouse selects text as ever.
  const press = useRef<{ i: number; x: number; y: number; moved: boolean; el: HTMLElement; timer: number } | null>(null);
  const swallow = useRef(false);
  // A line taken back is gone from the customer's phone: nothing there to quote.
  const quotable = (i: number) => !!onReply && !!messages[i]?.ref && !revokes.gone.has(i);
  function gestureDown(e: ReactPointerEvent<HTMLDivElement>, i: number) {
    // A hold that brought no click leaves nothing to swallow next time.
    swallow.current = false;
    if (e.pointerType !== "touch" || !onReply) return;
    const el = e.currentTarget;
    const timer = window.setTimeout(() => {
      if (press.current && !press.current.moved) {
        swallow.current = true;
        setActing(i);
        navigator.vibrate?.(10);
      }
    }, 450);
    press.current = { i, x: e.clientX, y: e.clientY, moved: false, el, timer };
  }
  function gestureMove(e: ReactPointerEvent<HTMLDivElement>) {
    const p = press.current;
    if (!p || e.pointerType !== "touch") return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    if (!p.moved && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      p.moved = true;
      window.clearTimeout(p.timer);
    }
    if (quotable(p.i) && dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      p.el.style.transform = `translateX(${Math.min(dx, 72)}px)`;
      p.el.classList.add("swiping");
      p.el.classList.toggle("swipe-go", dx > 56);
    }
  }
  function gestureUp(e: ReactPointerEvent<HTMLDivElement>) {
    const p = press.current;
    if (!p) return;
    press.current = null;
    window.clearTimeout(p.timer);
    const go = p.el.classList.contains("swipe-go");
    p.el.style.transform = "";
    p.el.classList.remove("swiping", "swipe-go");
    // A finger that moved brings no click: nothing to swallow.
    if (go && e.type === "pointerup" && quotable(p.i)) onReply?.(messages[p.i]!);
  }
  const theirFace = useMemo<Face>(
    () => ({ style: avatarStyle(peer.waID), initial: initialOf(peer.name || peer.waID), url: peer.avatarURL }),
    [peer.waID, peer.name, peer.avatarURL],
  );

  // The lines the console adds itself — 「weijian 接手了，小爱先不说话」 after 人工回复, 「交回小爱，她接着回」 after
  // 小爱回复 (chatNotes.ts, QA G2-02) — each after the last message before it, as v2.20's system lines sit.
  const notes = useChatNotes(peer.waID);
  const rows = useMemo(() => {
    const out: Row[] = [];
    const used = new Map<string, number>();
    const own = (k: string) => {
      const n = used.get(k) ?? 0;
      used.set(k, n + 1);
      return n ? `${k}#${n}` : k;
    };
    let n = 0;
    const note = (x: ChatNote) => out.push({ k: "note", n: x, key: `note-${x.id}`, at: new Date(x.at).toISOString() });
    messages.forEach((m, i) => {
      const t = Date.parse(m.at);
      while (n < notes.length && notes[n]!.at < t) note(notes[n++]!);
      out.push({ k: "msg", i, key: own(keyOf(m)), at: m.at });
    });
    while (n < notes.length) note(notes[n++]!);
    return out;
  }, [messages, notes]);

  // The newest FIRST_LINES rows are drawn, and MORE_LINES older ones each time the reader, having left the end,
  // scrolls within LINES_AHEAD of the top (the pane's scroll handler below). The reader stays on the line they were
  // reading: the pane keeps its distance from the bottom as rows join above it.
  const [drawn, setDrawn] = useState(FIRST_LINES);
  // A line that comes is drawn on top of those already drawn, not in place of the oldest: the top only moves when
  // the reader reads back (Safari keeps no reader's place when a line goes from above it).
  const [rowsBefore, setRowsBefore] = useState(rows.length);
  if (rows.length !== rowsBefore) {
    setRowsBefore(rows.length);
    if (rows.length > rowsBefore) setDrawn((d) => d + rows.length - rowsBefore);
  }
  const from = Math.max(0, rows.length - drawn);
  const rowAt = useMemo(() => new Map(rows.flatMap((r, ri) => (r.k === "msg" ? [[r.i, ri] as const] : []))), [rows]);
  const grewFrom = useRef<number | null>(null);
  const older = useRef<(() => void) | null>(null);
  older.current =
    from > 0
      ? () => {
          const p = paneRef.current;
          if (!p || grewFrom.current !== null) return;
          grewFrom.current = p.scrollHeight - p.scrollTop;
          setDrawn((d) => d + MORE_LINES);
        }
      : null;
  useLayoutEffect(() => {
    const p = paneRef.current;
    const g = grewFrom.current;
    grewFrom.current = null;
    if (p && g !== null) p.scrollTop = p.scrollHeight - g;
    const i = jumpOwed.current;
    jumpOwed.current = null;
    if (i !== null) goTo(i, true);
    // goTo is this render's: it reads nothing the effect has to follow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawn, paneRef]);

  // The chat's pictures, in order, for the viewer's ← →.
  const pics = useMemo(() => messages.flatMap((m, i) => (pictureShown(m) ? [i] : [])), [messages]);
  const [viewing, setViewing] = useState<number | null>(null);

  // A line that came after the chat opened slides up into place (prototype v2.20 .chat-new, QA G2-06); the
  // ones there when it opened do not, so switching chats does not set the whole thread moving. Keyed by the
  // row's own name, first seen at.
  const born = useRef<Map<string, number> | null>(null);
  const now = Date.now();
  const fresh = (key: string) => {
    const b = born.current;
    if (!b) return false;
    let t = b.get(key);
    if (t === undefined) {
      t = now;
      b.set(key, t);
    }
    return now - t < FRESH_MS;
  };

  const atBottom = useRef(true);
  // On its way down to a new line: still at the bottom for whatever comes meanwhile.
  const chasing = useRef(false);
  const settled = useRef(false);
  const last = messages[messages.length - 1];
  // Length alone misses the case where the server returns a capped window (the
  // count stops growing while the content keeps changing).
  const tail = `${messages.length}|${last?.at ?? ""}|${last?.text ?? ""}|${notes.length}`;
  // 对话 kept off screen behind another page: reading the pane there lays the whole page out for it, on the spot.
  const shown = useContext(PageShown);
  const shownNow = useRef(shown);
  shownNow.current = shown;
  // Down to the newest line once the pane has been laid out, never by asking it to be laid out now. The read used
  // to come in a frame of its own, and on a page off screen each picture that loaded laid all of 对话 out again: the
  // console opening on 今天 froze 0.9 s for the chat it reopens there by itself, then 0.2 s a picture (production,
  // 10/27). An element newly watched is reported after its next layout, so the read comes when it is free — and on
  // a page off screen not until that page is back, the very frame it is laid out to be shown.
  const owed = useRef<ResizeObserver | null>(null);
  // When the reader last wheeled, touched, keyed or pressed in the pane (performance.now()), and the lines' watcher.
  const readerAt = useRef(-Infinity);
  const rowsWatch = useRef<ResizeObserver | null>(null);
  const toBottom = useCallback(() => {
    const p = paneRef.current;
    if (!p || owed.current) return;
    const ro = new ResizeObserver(() => {
      ro.disconnect();
      owed.current = null;
      if (atBottom.current) p.scrollTop = p.scrollHeight;
    });
    owed.current = ro;
    ro.observe(p);
  }, [paneRef]);
  useEffect(() => () => owed.current?.disconnect(), []);

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    const onScroll = () => {
      // Off screen nobody scrolls it: the pane only moving as its page is put away is not the reader leaving the end.
      if (!shownNow.current) return;
      // At the end, and not moved by the reader: nothing below can change, so the pane's height is not asked for.
      // Asked for while the page was still being drawn, it laid the page out on the spot: 0.21–0.25 s of the
      // console's opening on the owner's screen (2026-10-28).
      if (atBottom.current && !chasing.current && performance.now() - readerAt.current >= READER_MS) return;
      const gap = pane.scrollHeight - pane.scrollTop - pane.clientHeight;
      if (chasing.current) {
        if (gap < 2) chasing.current = false;
        atBottom.current = true;
        return;
      }
      // Only the reader leaves the end. A picture growing under the newest line, or the jump to it, moves the pane
      // as well, and taken for scrolling up it left a chat opened fresh 112px short, its newest picture cut (QA R-05).
      if (gap < 40) atBottom.current = true;
      else if (performance.now() - readerAt.current < READER_MS) atBottom.current = false;
      // Reading back towards the top: the older lines join before they are reached (FIRST_LINES).
      if (!atBottom.current && pane.scrollTop < LINES_AHEAD) older.current?.();
    };
    // The reader's own wheel, finger, key or press on the bar: it ends the chase, and what scrolls next is theirs.
    const stop = () => {
      chasing.current = false;
      readerAt.current = performance.now();
    };
    pane.addEventListener("scroll", onScroll, { passive: true });
    const reader = ["wheel", "touchstart", "touchmove", "keydown", "pointerdown"];
    reader.forEach((e) => pane.addEventListener(e, stop, { passive: true }));
    // Every line watched for its height: whatever grows at the end under a reader who is there — a picture, a clip,
    // a card — is followed, with a load event or without one. Told after layout, so the read is free, and never for
    // a page off screen, whose lines are not laid out.
    const rowsRO = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      if (!atBottom.current) return;
      if (chasing.current) pane.scrollTo({ top: pane.scrollHeight, behavior: "smooth" });
      else pane.scrollTop = pane.scrollHeight;
    });
    rowsWatch.current = rowsRO;
    for (const el of pane.children) rowsRO?.observe(el, { box: "border-box" });
    // The pane getting shorter — the phone's keyboard coming up, the box
    // growing a line, a picture waiting over it — keeps the newest message in
    // view for whoever was reading it, as WhatsApp does; it used to slide
    // under the keyboard just as somebody started to answer it.
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      if (atBottom.current) pane.scrollTop = pane.scrollHeight;
    });
    ro?.observe(pane);
    // And a picture, a clip or a voice note taking its size as it loads: a chat opened fresh was left with its newest line
    // half under the box, 47px short of the bottom (QA R4-03). Whoever had scrolled up is left where they are.
    const sized = () => {
      if (atBottom.current) toBottom();
    };
    const media = ["load", "loadedmetadata", "error"];
    media.forEach((e) => pane.addEventListener(e, sized, true));
    return () => {
      pane.removeEventListener("scroll", onScroll);
      reader.forEach((e) => pane.removeEventListener(e, stop));
      media.forEach((e) => pane.removeEventListener(e, sized, true));
      ro?.disconnect();
      rowsRO?.disconnect();
      rowsWatch.current = null;
    };
  }, [paneRef, toBottom]);
  // The lines drawn since are watched too (observing one already watched does nothing).
  useEffect(() => {
    const pane = paneRef.current;
    const w = rowsWatch.current;
    if (pane && w) for (const el of pane.children) w.observe(el, { box: "border-box" });
  }, [tail, paneRef]);

  useLayoutEffect(() => {
    if (!settled.current) {
      // First paint: open at the newest message, as a chat does, with nothing moving. The pane is
      // the parent's element and its ref is attached after this child's layout effect runs, so the
      // jump waits a frame rather than reading a null ref.
      settled.current = true;
      born.current = new Map(rows.map((r) => [r.key, 0]));
      requestAnimationFrame(toBottom);
      return;
    }
    // Something new: down to it, gliding (prototype v2.20 scrollTo smooth) — when the reader was at the
    // bottom already, or just sent it. Someone reading back is left where they are. Off screen it is
    // there when the page is back, without the glide.
    const sent = following.delete(peer.waID);
    const p = paneRef.current;
    if (!p || (!atBottom.current && !sent)) return;
    if (stillWanted() || !shownNow.current) {
      atBottom.current = true;
      toBottom();
      return;
    }
    chasing.current = true;
    atBottom.current = true;
    p.scrollTo({ top: p.scrollHeight, behavior: "smooth" });
  }, [tail, paneRef]);

  if (rows.length === 0) return <div className="wc-sys">还没有消息</div>;

  // The ad they came from goes over the first line they sent from the moment they tapped it (prototype
  // v2.20 .chat-ref, QA G2-14); a returning buyer's new ad, over the line that came with it.
  const adFrom = ad?.at ? Date.parse(ad.at) - 120_000 : -Infinity;
  const adOn = ad ? messages.findIndex((m) => m.direction === "in" && Date.parse(m.at) >= adFrom) : -1;

  const view = viewing !== null ? messages[viewing] : undefined;
  const viewAt = viewing !== null ? pics.indexOf(viewing) : -1;

  let lastDay = "";
  return (
    <>
      {rows.slice(from).map((row, at) => {
        const ri = from + at;
        const isNew = fresh(row.key);
        const day = dayLabel(row.at);
        const newDay = day !== lastDay;
        lastDay = day;
        if (row.k === "note") {
          return (
            <Fragment key={row.key}>
              {newDay && <div className="wc-day">{day}</div>}
              <div className={`wc-sys${isNew ? " wc-new" : ""}`}>
                {row.n.text}
                <em>{fmtClock(row.at)}</em>
              </div>
            </Fragment>
          );
        }
        const i = row.i;
        const m = messages[i]!;
        const mine = m.direction !== "in";
        const prev = messages[i - 1];
        const next = messages[i + 1];
        // Messenger puts the sender's face beside the LAST bubble of an
        // incoming run, so the run needs its end marked as well as its start.
        const lastOfGroup =
          !next ||
          (next.direction !== "in") !== mine ||
          new Date(next.at).getTime() - new Date(m.at).getTime() > GROUP_GAP_MS ||
          dayLabel(next.at) !== day;
        // A run is one sender's: when the script hands to a colleague the
        // next bubble starts a new run with its own name on it, as a group
        // chat does when the speaker changes.
        const who = mine ? authorLabel(m) : "";
        const ai = bySiaoAi(m);
        const sys = mine && who === "系统";
        // A colleague, by name or from the phone; not the system's notices.
        const staff = mine && !ai && !sys;
        // Our face beside the first bubble of a run, as a group chat draws the
        // other speakers': 小爱's ✦ or the headset, so who answered is seen
        // before a word is read.
        const ourFace = ai ? AI_FACE : staff ? STAFF_FACE : null;
        // A grey line — a revoke, a reaction, 「这条只能在手机上看」, a line the
        // console added — carries no name, so the bubble after it starts the run
        // again. Without this a colleague who deleted 小爱's line and answered in
        // her place was read under her name (10/3–10/10: 24 lines in 9 chats,
        // 「癌症的人是可以喝的」 among them).
        // Her read goes over the reply it went with (ReadCard), and the reply starts a run under it.
        const first =
          newDay ||
          !prev ||
          readAt.has(i) ||
          rows[ri - 1]?.k === "note" ||
          describeMessage(prev).system ||
          (prev.direction !== "in") !== mine ||
          (mine && authorLabel(prev) !== who) ||
          new Date(m.at).getTime() - new Date(prev.at).getTime() > GROUP_GAP_MS;
        // A reaction that landed on a message in view is drawn there.
        if ((m.raw_type || "").toUpperCase() === "REACTION") {
          try {
            const o = JSON.parse((m.text ?? "").trim()) as { messageId?: string };
            if (o.messageId && placed.has(o.messageId)) return null;
          } catch {
            // fall through and render it as a note
          }
        }
        const mine2 = m.msg_id ? reactions.get(m.msg_id) : undefined;
        const d = describeMessage(m);
        const kind = (m.raw_type || "").toUpperCase();
        // Every voice row is one, recording or not.
        const voice = kind === "AUDIO" || kind === "VOICE";
        // A sticker sits on the wallpaper with no bubble, as the app draws it.
        const sticker = kind === "STICKER" && stickerDrawn(m);
        // A picture or a clip fills a bubble of its own width (prototype v2.20 .k-img / .k-video).
        const media = (kind === "IMAGE" && pictureShown(m)) || (kind === "VIDEO" && (!!m.media_key || !!safeMediaHref(m.media_url)));
        // 小爱's order card, drawn as the receipt it is (prototype v2.20 .k-order).
        const slip = mine && !voice && !media && !sticker ? orderSlip(d.text) : null;
        // WhatsApp's own ticks, from its reports: ✓ sent, grey ✓✓ on their phone, blue ✓✓ read, and a
        // red mark for one that never arrived. Pressed, the time and the ticks open when each happened.
        // An API from before the reports were read says nothing: the grey ✓✓ it always drew.
        const tick = mine ? m.tick : undefined;
        const meta = tick ? (
          <button
            type="button"
            className={`wc-meta wc-tickbtn t-${tick}`}
            aria-expanded={timesOf === i}
            aria-label={`${TICK_SAY[tick] ? `${TICK_SAY[tick]} · ` : ""}看几点送到、几点读的`}
            title="看几点送到、几点读的"
            onClick={(e) => {
              e.stopPropagation();
              setTimesOf(timesOf === i ? null : i);
            }}
          >
            {fmtClock(m.at)}
            <TickIcon tick={tick} />
          </button>
        ) : (
          <span className="wc-meta">
            {fmtClock(m.at)}
            {mine && <ShellIcon name="check2" size={15} className="wc-tick" />}
          </span>
        );
        // The time on a line of its own under a voice note, or a picture with no words under it
        // (prototype .chat-foot); beside the last words everywhere else. Messenger's voice pill has
        // its own place for it.
        const footMeta = (voice && !fb) || (media && !d.text) || !!slip;
        // A system note — a reaction, a revoke, an edit mirrored from the
        // phone — is not a message anybody sent; it reads as WhatsApp's own
        // centred grey line rather than a bubble.
        if (d.system) {
          // A revoke says whose, and goes to the line it took back when that line is here.
          const took = revokes.took.get(i);
          const hers = revokes.hers.has(i);
          const said = !revokedID(m)
            ? d.text
            : m.direction === "in"
              ? "客户撤回了一条消息"
              : hers
                ? "客服在手机上撤回了小爱的一句"
                : "客服在手机上撤回了一条消息";
          return (
            <Fragment key={row.key}>
              {newDay && <div className="wc-day">{day}</div>}
              {took !== undefined ? (
                <button type="button" className={`wc-sys go${isNew ? " wc-new" : ""}`} title="看撤回的是哪句" onClick={() => jumpTo(took)}>
                  {said}
                  <ShellIcon name="up" size={13} />
                </button>
              ) : (
                <div className={`wc-sys${hers ? " ai" : ""}${isNew ? " wc-new" : ""}`} title={hers ? "WhatsApp 没说是哪一句" : undefined}>
                  {hers && <ShellIcon name="sparkles" size={12} />}
                  {said}
                </div>
              )}
            </Fragment>
          );
        }
        const gone = revokes.gone.has(i);
        return (
          <Fragment key={row.key}>
            {newDay && <div className="wc-day">{day}</div>}
            {readAt.has(i) && <ReadCard row={readAt.get(i)!} trials={trials} open={openRead === i} onToggle={() => setOpenRead(openRead === i ? null : i)} />}
            <div
              data-i={i}
              className={`wc-msg ${mine ? "out" : "in"}${ai ? " ai" : ""}${first ? " first" : ""}${mine2 && mine2.length > 0 ? " reacted" : ""}${gone ? " revoked" : ""}${flash === i ? " flash" : ""}${acting === i ? " act" : ""}${isNew ? " wc-new" : ""}`}
            >
              {!mine &&
                (lastOfGroup ? (
                  <span className="wc-face" style={theirFace.style} aria-hidden="true">
                    <AvatarPic url={theirFace.url} />
                    {theirFace.initial}
                  </span>
                ) : (
                  // Keeps the bubbles of a run in one column under the face.
                  <span className="wc-face ghost" aria-hidden="true" />
                ))}
              {/* Bubble and choices in one column, so the choices sit under
                  the bubble at its width, as WhatsApp draws them — not in a
                  column beside it. */}
              <div
                className="wc-stack"
                onPointerDown={(e) => gestureDown(e, i)}
                onPointerMove={gestureMove}
                onPointerUp={gestureUp}
                onPointerCancel={gestureUp}
                onClickCapture={(e) => {
                  if (!swallow.current) return;
                  swallow.current = false;
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
              {quotable(i) && (
                <span className="wc-swipe-ic" aria-hidden="true">
                  <ShellIcon name="reply" size={18} />
                </span>
              )}
              {/* 回复 and 撤回 beside the bubble, laid over the chat so no
                  bubble moves for them: on hover at a desk, held down on a
                  phone. Ours sit to its left, theirs to its right. */}
              <LineActions
                m={m}
                mine={mine}
                onReply={quotable(i) ? onReply : undefined}
                onReplay={m.author === "ai" && m.direction !== "in" ? onReplay : undefined}
              />
              <div
                className={`wc-bub${voice ? " voice" : ""}${voice && fb ? " mv" : ""}${sticker ? " sticker" : ""}${media ? (kind === "IMAGE" ? " k-img" : " k-video") : ""}${slip ? " k-order" : ""}`}
              >
                {/* The line this one answers, at the top of the bubble as both apps draw a reply; pressed, the chat goes to it. */}
                {m.reply_to && byRef.has(m.reply_to) && (
                  <Quote m={messages[byRef.get(m.reply_to)!]!} peerName={peer.name} onJump={() => jumpTo(byRef.get(m.reply_to!)!)} />
                )}
                {/* The sender's name on the first bubble of a run, where a
                    group chat puts it: the agents could not tell the
                    script from the AI from each other. A voice note carries
                    it every time — the player looks the same whoever sent
                    it, and 「是不是小爱回的」 was the question. */}
                {mine && (first || voice) && (
                  <span className={`wc-who${sys ? " sys" : ""}`}>
                    {ai ? (
                      <ShellIcon name="sparkles" size={12} />
                    ) : sys ? (
                      <ShellIcon name="bell" size={12} />
                    ) : (
                      <ShellIcon name={m.author === "phone" ? "phone" : "headset"} size={12} />
                    )}
                    {who}
                    {/* A colleague by name sent it from here: 「weijian · 控制台」, as 「客服 · 手机」 says the phone (prototype v2.20 .chat-by). */}
                    {staff && !!m.author && who === m.author && " · 控制台"}
                  </span>
                )}
                {/* Taken back, and still here to be read (owner, 10/31): faded under this. */}
                {gone && (
                  <span className="wc-taken">
                    <ShellIcon name="ban" size={12} />
                    已撤回
                  </span>
                )}
                {/* The ad they tapped, quoted at the top of the line it brought. */}
                {i === adOn && ad && (
                  <span className="wc-ref">
                    <AdPic src={ad.thumb} size={40} />
                    <span>
                      <b>{ad.name}</b>
                      <small>
                        {fb ? "Messenger" : "Facebook"} 广告{ad.sub ? ` · ${ad.sub}` : ""}
                      </small>
                    </span>
                  </span>
                )}
                <Attachment m={m} fb={fb} onView={kind === "IMAGE" ? () => setViewing(i) : undefined} />
                {slip ? <OrderSlipView slip={slip} at={m.at} /> : d.text && <span className="wc-txt">{d.text}</span>}
                {/* What was said in a voice note, under it: 「转文字」 for one somebody spoke, 「转语音念的」 for
                    words 小爱 or 转语音 had read out (prototype v2.20 .chat-tr). */}
                {d.sub && (
                  <span className="wc-txt sub">
                    {voice ? `${/^\s*🎤/.test(m.text ?? "") ? "转语音念的" : "转文字"}：` : ""}
                    {d.sub}
                  </span>
                )}
                {footMeta ? <span className="wc-mfoot">{meta}</span> : meta}
                {/* The emoji sits on the bubble's lower edge, as it does in
                    both apps, rather than pushing the thread around. */}
                {mine2 && mine2.length > 0 && <span className="wc-react">{mine2.join("")}</span>}
              </div>
              {/* What the customer could tap. WhatsApp draws these as pale
                  full-width pills under the bubble; without them the thread
                  showed the words and no sign that anything was offered. */}
              {m.buttons && m.buttons.length > 0 && (
                <div className="wc-choices" title="客户看到的按钮">
                  {m.buttons.map((b, k) => (
                    <span className={`wc-choice${pressed.get(i) === b ? " on" : ""}`} key={k}>
                      {/* 「✅ 确认下单」 carries its own tick. */}
                      {pressed.get(i) === b && !/^[✅✔☑]/.test(b) && <ShellIcon name="check" size={14} />}
                      {b}
                    </span>
                  ))}
                </div>
              )}
              {/* The stand-in wrote this one: owner, 2026-10-10 — was a wrong answer 3.8 Flash's, or the
                  backup's? Once a run, under it, with the model's short name. */}
              {m.backup && next?.backup !== m.backup && (
                <span className="wc-backup">
                  <ShellIcon name="alert" size={12} />
                  这句由备用 {modelName(m.backup)} 回
                </span>
              )}
              {tick && timesOf === i && <TickTimes m={m} />}
              {i === lastRead && m.read_at && (
                <span className="wc-seen">
                  <ShellIcon name="check2" size={14} />
                  {m.read_by_reply ? `客户 ${shortWhen(m.read_at)} 回了话` : `已读 ${shortWhen(m.read_at)}`}
                </span>
              )}
              </div>
              {mine &&
                (first && ourFace ? (
                  <span className={`wc-our-face${ai ? " ai" : ""}`} style={ourFace.style} title={ai ? "小爱" : "同事"} aria-hidden="true">
                    {ourFace.icon}
                  </span>
                ) : (
                  <span className="wc-our-face ghost" aria-hidden="true" />
                ))}
            </div>
          </Fragment>
        );
      })}
      {view && viewAt >= 0 && (
        <ChatViewer
          pic={<ViewerPic key={keyOf(view)} m={view} />}
          caption={describeMessage(view).text || "图片"}
          by={`${view.direction === "in" ? peer.name || "客户" : authorLabel(view)} · ${shortWhen(view.at)}`}
          pos={pics.length > 1 ? `${viewAt + 1} / ${pics.length}` : undefined}
          onPrev={viewAt > 0 ? () => setViewing(pics[viewAt - 1]!) : undefined}
          onNext={viewAt < pics.length - 1 ? () => setViewing(pics[viewAt + 1]!) : undefined}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}

/** Who a quote names: the customer by name, ours by who sent it. */
export function quoteWho(m: Message, peerName: string): string {
  return m.direction === "in" ? peerName || "客户" : authorLabel(m);
}

/** The words of a quoted line, or what it was when it has none. */
export function quoteText(m: Message): string {
  const d = describeMessage(m);
  if (d.text) return d.text;
  return d.icon === "image" ? "图片" : d.icon === "video" ? "视频" : d.icon === "audio" ? "语音" : "附件";
}

/**
 * The line a reply answers, at the top of its bubble as WhatsApp draws it: a
 * bar in the speaker's colour, their name, the first lines of what they said.
 */
function Quote({ m, peerName, onJump }: { m: Message; peerName: string; onJump: () => void }) {
  const d = describeMessage(m);
  const tone = m.direction === "in" ? "them" : bySiaoAi(m) ? "ai" : "us";
  return (
    <button
      type="button"
      className={`wc-q q-${tone}`}
      title="跳到这句"
      onClick={(e) => {
        e.stopPropagation();
        onJump();
      }}
    >
      <b>{quoteWho(m, peerName)}</b>
      <span>
        {d.icon && <MediaIcon kind={d.icon} />}
        {quoteText(m)}
      </span>
    </button>
  );
}

/** 回复: a small round button beside the bubble. */
function LineActions({
  m,
  mine,
  onReply,
  onReplay,
}: {
  m: Message;
  mine?: boolean;
  onReply?: (m: Message) => void;
  onReplay?: (m: Message) => void;
}) {
  if (!onReply && !onReplay) return null;
  return (
    <span className={`wc-acts${mine ? " mine" : ""}`}>
      {onReplay && (
        <button type="button" className="wc-act" title="用草稿重跑：这一句草稿会怎么回（不会发给客户）" aria-label="用草稿重跑" onClick={() => onReplay(m)}>
          <ShellIcon name="refresh" size={16} />
        </button>
      )}
      {onReply && (
        <button type="button" className="wc-act" title="回复这句" aria-label="回复这句" onClick={() => onReply(m)}>
          <ShellIcon name="reply" size={16} />
        </button>
      )}
    </span>
  );
}
